import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import Layout from '../components/layout/Layout';
import { API_BASE } from '../api';

function UpdateTask() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get task data from navigation state
  const initialTaskData = location.state?.taskData || null;
  
  const [formData, setFormData] = useState({
    raw_drive_link: '',
    video_name: '',
    count: '',
    reference_video_link: '',
    client_id: '',
    shoot_date: '',
    deadline_date: '',
    notes: '',
    status: 'pending',
    video_type: '' // New field for video type
  });
  
  const [clients, setClients] = useState([]);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(!initialTaskData);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const { currentTheme } = useContext(ThemeContext);

  // Video type options
  const videoTypeOptions = [
    { value: '', label: 'Select video type...' },
    { value: 'indoor_reel', label: '🏠 Indoor Reel' },
    { value: 'outdoor_reel', label: '🌳 Outdoor Reel' },
    { value: 'podcast', label: '🎙️ Podcast' },
    { value: 'indoor_long_video', label: '🏠 Indoor Long Video' },
    { value: 'outdoor_long_video', label: '🌳 Outdoor Long Video' }
  ];

  // Debug: Log what we receive
  useEffect(() => {
    console.log('📍 UpdateTask mounted with location state:', location.state);
    console.log('📋 Task data received:', location.state?.taskData);
    console.log('👤 Client ID received:', location.state?.taskData?.client_id);
    console.log('👤 Client Name received:', location.state?.taskData?.client_name);
    console.log('🎥 Video Type received:', location.state?.taskData?.video_type);
  }, [location.state]);

  useEffect(() => {
    fetchClients();
    
    if (initialTaskData) {
      console.log('Using initial task data:', initialTaskData);
      populateFormWithTaskData(initialTaskData);
    } else {
      fetchTask();
    }
  }, [taskId, initialTaskData]);

  const populateFormWithTaskData = (taskData) => {
    console.log('📥 Populating form with task data:', taskData);
    console.log('👤 Setting client_id to:', taskData.client_id);
    console.log('🎥 Setting video_type to:', taskData.video_type);
    
    setFormData({
      raw_drive_link: taskData.raw_drive_link || taskData.raw_link || '',
      video_name: taskData.video_name || '',
      count: taskData.count || taskData.video_count || '',
      reference_video_link: taskData.reference_video_link || '',
      client_id: taskData.client_id || '',
      shoot_date: taskData.shoot_date ? formatDateForInput(taskData.shoot_date) : '',
      deadline_date: taskData.deadline_date ? formatDateForInput(taskData.deadline_date) : '',
      notes: taskData.notes || '',
      status: taskData.status || 'pending',
      video_type: taskData.video_type || '' // Set video type from task data
    });
    setLoading(false);
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Auto-fill video type based on video name
  const autoFillVideoType = (videoName) => {
    const name = videoName.toLowerCase();
    
    if (name.includes('reel')) {
      if (name.includes('indoor') || name.includes('studio') || name.includes('inside')) {
        return 'indoor_reel';
      } else if (name.includes('outdoor') || name.includes('outside') || name.includes('location')) {
        return 'outdoor_reel';
      }
      return name.includes('outdoor') ? 'outdoor_reel' : 'indoor_reel';
    }
    
    if (name.includes('podcast') || name.includes('interview') || name.includes('talk')) {
      return 'podcast';
    }
    
    if (name.includes('long') || name.includes('full') || name.includes('extended')) {
      if (name.includes('indoor') || name.includes('studio') || name.includes('inside')) {
        return 'indoor_long_video';
      } else if (name.includes('outdoor') || name.includes('outside') || name.includes('location')) {
        return 'outdoor_long_video';
      }
    }
    
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));

    // Auto-fill video type when video name changes
    if (name === 'video_name' && value.trim() && !formData.video_type) {
      const suggestedVideoType = autoFillVideoType(value);
      if (suggestedVideoType) {
        setTimeout(() => {
          setFormData(prev => ({
            ...prev,
            video_type: suggestedVideoType
          }));
          setMessage(`✅ Auto-filled video type: ${videoTypeOptions.find(opt => opt.value === suggestedVideoType)?.label}`);
          setTimeout(() => setMessage(''), 3000);
        }, 500);
      }
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_clients.php`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setClients(data);
      setClientsLoaded(true);
      console.log('📋 Clients fetched:', data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setMessage('Error loading clients: ' + error.message);
      setClientsLoaded(true);
    }
  };

  const fetchTask = async () => {
    try {
      setLoading(true);
      const operatorId = sessionStorage.getItem('userId') || 1;
      
      console.log('Fetching task details for ID:', taskId);
      
      const requestBody = {
        operation: 'get_task',
        id: parseInt(taskId),
        operator_id: parseInt(operatorId)
      };
      
      const response = await fetch(`${API_BASE}/update_tasks.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const responseText = await response.text();
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}, response: ${responseText}`);
      }
      
      const data = responseText ? JSON.parse(responseText) : null;
      
      if (data && !data.success) {
        setMessage('❌ ' + (data.message || 'Task not found'));
      } else if (data) {
        populateFormWithTaskData(data);
      } else {
        setMessage('❌ Task not found or invalid response');
      }
    } catch (error) {
      console.error('Error fetching task:', error);
      setMessage('❌ Error fetching task: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    if (!formData.raw_drive_link || !formData.client_id || !formData.shoot_date) {
      setMessage('❌ Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    try {
      const operatorId = sessionStorage.getItem('userId') || 1;
      
      const response = await fetch(`${API_BASE}/update_tasks.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'update_task',
          id: parseInt(taskId),
          raw_drive_link: formData.raw_drive_link,
          video_name: formData.video_name,
          count: formData.count,
          reference_video_link: formData.reference_video_link,
          client_id: parseInt(formData.client_id),
          shoot_date: formData.shoot_date,
          deadline_date: formData.deadline_date || null,
          notes: formData.notes,
          status: formData.status,
          video_type: formData.video_type, // Include video type in submission
          operator_id: parseInt(operatorId)
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage('✅ Task updated successfully! Redirecting...');
        setTimeout(() => {
          navigate('/view-tasks');
        }, 2000);
      } else {
        setMessage('❌ ' + (data.message || 'Error updating task'));
      }
    } catch (error) {
      console.error('Error updating task:', error);
      setMessage('❌ Error updating task: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get current client name for display
  const getCurrentClientName = () => {
    if (!formData.client_id || clients.length === 0) return '';
    
    const selectedClient = clients.find(client => 
      client.id.toString() === formData.client_id.toString()
    );
    
    return selectedClient ? selectedClient.name : 'Client not found';
  };

  // Get the client name that was passed from ViewTasks
  const getOriginalClientName = () => {
    return initialTaskData?.client_name || '';
  };

  // Get current video type label
  const getCurrentVideoTypeLabel = () => {
    const currentType = videoTypeOptions.find(opt => opt.value === formData.video_type);
    return currentType ? currentType.label : 'Not selected';
  };

  // Enhanced dropdown styles with dark theme
  const dropdownStyles = {
    container: {
      position: 'relative',
      width: '100%'
    },
    select: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '12px',
      padding: '15px 45px 15px 20px',
      color: 'white',
      fontSize: '1rem',
      width: '100%',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: 'right 15px center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '16px',
      cursor: 'pointer'
    }
  };

  // Status options with icons and colors
  const statusOptions = [
    { value: 'pending', label: '⏳ Pending', color: '#F59E0B' },
    { value: 'in_progress', label: '🔄 In Progress', color: '#06B6D4' },
    { value: 'completed', label: '✅ Completed', color: '#10B981' },
    { value: 'needs_revision', label: '✏️ Needs Revision', color: '#EF4444' }
  ];

  // Styles matching your dark theme
  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
      position: 'relative',
      overflowX: 'hidden'
    },
    animatedBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none'
    },
    floatingOrb: {
      position: 'absolute',
      borderRadius: '50%',
      filter: 'blur(40px)',
      opacity: 0.3,
      animation: 'float 8s ease-in-out infinite'
    },
    orb1: {
      width: '300px',
      height: '300px',
      background: '#6366F1',
      top: '10%',
      left: '5%',
      animationDelay: '0s'
    },
    orb2: {
      width: '200px',
      height: '200px',
      background: '#8B5CF6',
      top: '60%',
      right: '10%',
      animationDelay: '2s'
    },
    orb3: {
      width: '250px',
      height: '250px',
      background: '#06B6D4',
      bottom: '20%',
      left: '15%',
      animationDelay: '4s'
    },
    orb4: {
      width: '180px',
      height: '180px',
      background: '#10B981',
      top: '30%',
      right: '20%',
      animationDelay: '6s'
    },
    gridOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                       linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
      backgroundSize: '50px 50px'
    },
    header: {
      padding: '40px 40px 30px',
      position: 'relative',
      zIndex: 2
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    greeting: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      marginBottom: '15px'
    },
    pageTitle: {
      fontSize: '3rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      margin: 0
    },
    welcomeBadge: {
      background: 'rgba(255, 255, 255, 0.1)',
      padding: '8px 16px',
      borderRadius: '20px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    badgeText: {
      color: 'white',
      fontWeight: '600',
      fontSize: '0.9rem'
    },
    badgeRole: {
      background: '#6366F1',
      color: 'white',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '0.7rem',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    pageSubtitle: {
      color: 'rgba(255, 255, 255, 0.8)',
      fontSize: '1.2rem',
      margin: 0,
      maxWidth: '600px'
    },
    messageAlert: {
      color: 'white',
      padding: '20px',
      borderRadius: '15px',
      margin: '0 40px 30px',
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      maxWidth: '1200px',
      marginLeft: 'auto',
      marginRight: 'auto'
    },
    alertIcon: {
      fontSize: '1.5rem'
    },
    alertMessage: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      color: 'white'
    },
    alertClose: {
      background: 'none',
      border: 'none',
      color: '#EF4444',
      cursor: 'pointer',
      padding: '8px',
      borderRadius: '8px',
      transition: 'background 0.3s ease'
    },
    content: {
      padding: '0 40px 40px',
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 2
    },
    formSection: {
      marginBottom: '40px'
    },
    formCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '25px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
    },
    cardHeader: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '25px 30px'
    },
    cardTitle: {
      fontSize: '1.8rem',
      fontWeight: '700',
      margin: 0
    },
    cardBody: {
      padding: '40px'
    },
    formGroup: {
      marginBottom: '30px'
    },
    formLabel: {
      color: 'white',
      fontWeight: '600',
      fontSize: '1rem',
      marginBottom: '10px',
      display: 'block'
    },
    formInput: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '12px',
      padding: '15px 20px',
      color: 'white',
      fontSize: '1rem',
      width: '100%',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)'
    },
    formText: {
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: '0.85rem',
      marginTop: '8px'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '20px'
    },
    submitBtn: {
      color: 'white',
      border: 'none',
      padding: '18px 30px',
      borderRadius: '15px',
      fontWeight: '700',
      fontSize: '1.1rem',
      width: '100%',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px'
    },
    spinner: {
      width: '20px',
      height: '20px',
      border: '2px solid transparent',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    backBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      padding: '12px 24px',
      borderRadius: '12px',
      fontWeight: '600',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      textDecoration: 'none'
    },
    loadingState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '100px 40px',
      color: 'white',
      textAlign: 'center'
    },
    loadingText: {
      fontSize: '2rem',
      margin: '0 0 20px 0',
      fontWeight: '600',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    loadingSubtext: {
      color: 'rgba(255, 255, 255, 0.8)',
      margin: 0,
      fontSize: '1.2rem'
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '0.8rem',
      fontWeight: '600',
      marginLeft: '10px'
    },
    clientInfo: {
      background: 'rgba(99, 102, 241, 0.1)',
      border: '1px solid rgba(99, 102, 241, 0.3)',
      borderRadius: '8px',
      padding: '10px 15px',
      marginTop: '8px',
      fontSize: '0.85rem'
    },
    videoTypeSuggestion: {
      background: 'rgba(34, 197, 94, 0.1)',
      border: '1px solid rgba(34, 197, 94, 0.3)',
      borderRadius: '8px',
      padding: '10px 15px',
      marginTop: '8px',
      fontSize: '0.85rem',
      color: '#22c55e'
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div className="animated-background" style={styles.animatedBackground}>
            <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
            <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
            <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
            <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
            <div className="grid-overlay" style={styles.gridOverlay}></div>
          </div>
          <div style={styles.loadingState}>
            <div className="loading-spinner" style={styles.spinner}></div>
            <h3 style={styles.loadingText}>Loading Task Details</h3>
            <p style={styles.loadingSubtext}>Please wait while we fetch the task information</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        {/* Animated Background */}
        <div className="animated-background" style={styles.animatedBackground}>
          <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
          <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
          <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
          <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
          <div className="grid-overlay" style={styles.gridOverlay}></div>
        </div>

        {/* Header Section */}
        <div className="update-task-header" style={styles.header}>
          <div className="header-content" style={styles.headerContent}>
            <div className="welcome-section">
              <div className="greeting" style={styles.greeting}>
                <h1 className="page-title" style={styles.pageTitle}>
                  <i className="fas fa-edit me-2"></i>
                  Update Task #{taskId}
                </h1>
                <div className="welcome-badge" style={styles.welcomeBadge}>
                  <span className="badge-text" style={styles.badgeText}>Task Management</span>
                  <span className="badge-role" style={styles.badgeRole}>Editor</span>
                </div>
              </div>
              <p className="page-subtitle" style={styles.pageSubtitle}>
                Update and modify task details with the form below
              </p>
            </div>
            <div style={{marginTop: '20px'}}>
              <button 
                onClick={() => navigate('/view-tasks')}
                style={styles.backBtn}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back to Tasks
              </button>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div className="message-alert" style={{
            ...styles.messageAlert,
            background: message.includes('✅') 
              ? 'rgba(16, 185, 129, 0.1)' 
              : 'rgba(220, 53, 69, 0.1)',
            border: message.includes('✅') 
              ? '1px solid #10B981'
              : '1px solid #EF4444',
            color: message.includes('✅') 
              ? '#10B981'
              : '#EF4444'
          }}>
            <div className="alert-icon" style={{...styles.alertIcon, 
              color: message.includes('✅') 
                ? '#10B981'
                : '#EF4444'
            }}>
              <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
            </div>
            <div className="alert-message" style={styles.alertMessage}>
              <strong>{message.includes('✅') ? 'Success' : 'Error'}</strong>
              <span style={{color: 'white'}}>{message}</span>
            </div>
            <button className="alert-close" onClick={() => setMessage('')} style={styles.alertClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="update-task-content" style={styles.content}>
          <div className="form-section" style={styles.formSection}>
            <div className="form-card" style={styles.formCard}>
              <div className="card-header" style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  <i className="fas fa-edit me-2"></i>
                  Update Task Details
                </h3>
              </div>
              <div className="card-body" style={styles.cardBody}>
                <form onSubmit={handleSubmit}>
                  {/* Raw Drive Link */}
                  <div className="form-group" style={styles.formGroup}>
                    <label htmlFor="raw_drive_link" className="form-label" style={styles.formLabel}>
                      <i className="fas fa-link me-2"></i>
                      Raw Drive Link *
                    </label>
                    <input
                      type="url"
                      className="form-input"
                      id="raw_drive_link"
                      name="raw_drive_link"
                      value={formData.raw_drive_link}
                      onChange={handleInputChange}
                      placeholder="https://www.youtube.com/watch?v=..."
                      required
                      style={styles.formInput}
                    />
                    <div className="form-text" style={styles.formText}>
                      Enter the YouTube or drive link for raw footage
                    </div>
                  </div>

                  {/* Count and Video Name */}
                  <div className="form-row" style={styles.formRow}>
                    <div className="form-group" style={styles.formGroup}>
                      <label htmlFor="count" className="form-label" style={styles.formLabel}>
                        <i className="fas fa-hashtag me-2"></i>
                        Count
                      </label>
                      <input
                        type="number"
                        className="form-input"
                        id="count"
                        name="count"
                        value={formData.count}
                        onChange={handleInputChange}
                        placeholder="Enter count number"
                        min="0"
                        style={styles.formInput}
                      />
                      <div className="form-text" style={styles.formText}>
                        Enter the count/number for this task
                      </div>
                    </div>
                    <div className="form-group" style={styles.formGroup}>
                      <label htmlFor="video_name" className="form-label" style={styles.formLabel}>
                        <i className="fas fa-film me-2"></i>
                        Video Name (optional)
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        id="video_name"
                        name="video_name"
                        value={formData.video_name}
                        onChange={handleInputChange}
                        placeholder="e.g., Spring Company Tower"
                        style={styles.formInput}
                      />
                      <div className="form-text" style={styles.formText}>
                        Video type will be auto-suggested based on name
                      </div>
                    </div>
                  </div>

                  {/* Video Type Dropdown */}
                  <div className="form-group" style={styles.formGroup}>
                    <label htmlFor="video_type" className="form-label" style={styles.formLabel}>
                      <i className="fas fa-video me-2"></i>
                      Video Type
                    </label>
                    <div style={dropdownStyles.container}>
                      <select
                        className="form-input"
                        id="video_type"
                        name="video_type"
                        value={formData.video_type}
                        onChange={handleInputChange}
                        style={dropdownStyles.select}
                      >
                        {videoTypeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-text" style={styles.formText}>
                      {formData.video_type ? (
                        <span>
                          Selected: <strong>{getCurrentVideoTypeLabel()}</strong>
                        </span>
                      ) : (
                        'Select the type of video content'
                      )}
                      {formData.video_name && !formData.video_type && (
                        <div style={styles.videoTypeSuggestion}>
                          <i className="fas fa-lightbulb me-2"></i>
                          <strong>Tip:</strong> Video type will auto-fill based on video name keywords
                          <div style={{fontSize: '0.8rem', marginTop: '5px', opacity: 0.8}}>
                            Try including: "reel", "podcast", "long video", "indoor", "outdoor"
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Reference Video Link */}
                  <div className="form-group" style={styles.formGroup}>
                    <label htmlFor="reference_video_link" className="form-label" style={styles.formLabel}>
                      <i className="fas fa-external-link-alt me-2"></i>
                      Reference Video Link (optional)
                    </label>
                    <input
                      type="url"
                      className="form-input"
                      id="reference_video_link"
                      name="reference_video_link"
                      value={formData.reference_video_link}
                      onChange={handleInputChange}
                      placeholder="https://..."
                      style={styles.formInput}
                    />
                  </div>

                  {/* Client, Dates and Status */}
                  <div className="form-row" style={styles.formRow}>
                    <div className="form-group" style={styles.formGroup}>
                      <label htmlFor="client_id" className="form-label" style={styles.formLabel}>
                        <i className="fas fa-user-tie me-2"></i>
                        Client *
                      </label>
                      <div style={dropdownStyles.container}>
                        <select
                          className="form-input"
                          id="client_id"
                          name="client_id"
                          value={formData.client_id}
                          onChange={handleInputChange}
                          required
                          style={dropdownStyles.select}
                        >
                          <option value="">Select client...</option>
                          {clientsLoaded && clients.length > 0 ? (
                            clients.map(client => (
                              <option key={client.id} value={client.id}>
                                {client.name}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>
                              {clientsLoaded ? 'No clients available' : 'Loading clients...'}
                            </option>
                          )}
                        </select>
                      </div>
                      <div className="form-text" style={styles.formText}>
                        {!clientsLoaded ? (
                          'Loading clients list...'
                        ) : clients.length === 0 ? (
                          'No clients available'
                        ) : (
                          `${clients.length} client(s) available`
                        )}
                        
                        {/* Show the original client name from ViewTasks */}
                        {getOriginalClientName() && (
                          <div style={styles.clientInfo}>
                            <strong>Original Client:</strong> {getOriginalClientName()}
                          </div>
                        )}
                        
                        {/* Show current selection */}
                        {formData.client_id && clientsLoaded && clients.length > 0 && (
                          <div style={{marginTop: '5px'}}>
                            <strong>Currently Selected:</strong> {getCurrentClientName()}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-group" style={styles.formGroup}>
                      <label htmlFor="shoot_date" className="form-label" style={styles.formLabel}>
                        <i className="fas fa-calendar-alt me-2"></i>
                        Shoot Date *
                      </label>
                      <input
                        type="date"
                        className="form-input"
                        id="shoot_date"
                        name="shoot_date"
                        value={formData.shoot_date}
                        onChange={handleInputChange}
                        required
                        style={styles.formInput}
                      />
                    </div>

                    <div className="form-group" style={styles.formGroup}>
                      <label htmlFor="deadline_date" className="form-label" style={styles.formLabel}>
                        <i className="fas fa-clock me-2"></i>
                        Deadline Date (optional)
                      </label>
                      <input
                        type="date"
                        className="form-input"
                        id="deadline_date"
                        name="deadline_date"
                        value={formData.deadline_date}
                        onChange={handleInputChange}
                        style={styles.formInput}
                      />
                      <div className="form-text" style={styles.formText}>
                        Optional deadline date
                      </div>
                    </div>
                  </div>

                  {/* Enhanced Status Field with Styled Dropdown */}
                  <div className="form-group" style={styles.formGroup}>
                    <label htmlFor="status" className="form-label" style={styles.formLabel}>
                      <i className="fas fa-flag me-2"></i>
                      Status
                    </label>
                    <div style={dropdownStyles.container}>
                      <select
                        className="form-input"
                        id="status"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        style={{
                          ...dropdownStyles.select,
                          borderColor: statusOptions.find(opt => opt.value === formData.status)?.color || 'rgba(255, 255, 255, 0.2)'
                        }}
                      >
                        {statusOptions.map(option => (
                          <option 
                            key={option.value} 
                            value={option.value}
                            style={{
                              background: '#302b63',
                              color: 'white',
                              padding: '10px'
                            }}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-text" style={styles.formText}>
                      Current status: 
                      <span 
                        style={{
                          ...styles.statusBadge,
                          background: statusOptions.find(opt => opt.value === formData.status)?.color + '20',
                          color: statusOptions.find(opt => opt.value === formData.status)?.color,
                          border: `1px solid ${statusOptions.find(opt => opt.value === formData.status)?.color}40`
                        }}
                      >
                        {statusOptions.find(opt => opt.value === formData.status)?.label}
                      </span>
                    </div>
                  </div>

                  {/* Notes Field */}
                  <div className="form-group" style={styles.formGroup}>
                    <label htmlFor="notes" className="form-label" style={styles.formLabel}>
                      <i className="fas fa-sticky-note me-2"></i>
                      Notes (optional)
                    </label>
                    <textarea
                      className="form-input"
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Any specific requests or instructions..."
                      style={{...styles.formInput, resize: 'vertical'}}
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <div className="form-group" style={styles.formGroup}>
                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={isSubmitting || !clientsLoaded || clients.length === 0}
                      style={{
                        ...styles.submitBtn,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        opacity: (isSubmitting || !clientsLoaded || clients.length === 0) ? 0.6 : 1,
                        cursor: (isSubmitting || !clientsLoaded || clients.length === 0) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner" style={styles.spinner}></span>
                          Updating Task...
                        </>
                      ) : !clientsLoaded ? (
                        <>
                          <i className="fas fa-spinner fa-spin me-2"></i>
                          LOADING CLIENTS...
                        </>
                      ) : clients.length === 0 ? (
                        <>
                          <i className="fas fa-exclamation-triangle me-2"></i>
                          NO CLIENTS AVAILABLE
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          UPDATE TASK
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes float {
          0%, 100% { 
            transform: translate(0px, 0px) scale(1); 
          }
          33% { 
            transform: translate(30px, -50px) scale(1.1); 
          }
          66% { 
            transform: translate(-20px, 20px) scale(0.9); 
          }
        }

        @keyframes wave {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }
        
        /* Custom Hover Effects */
        .form-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
        }
        
        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
        }
        
        .back-btn:hover {
          background: rgba(255, 255, 255, 0.2) !important;
          transform: translateY(-2px);
        }
        
        /* Form input focus states */
        .form-input:focus {
          background: rgba(255, 255, 255, 0.15);
          border-color: #6366F1;
          color: white;
          box-shadow: 0 0 0 0.2rem rgba(99, 102, 241, 0.25);
          outline: none;
        }

        .form-input::placeholder {
          color: rgba(255, 255, 255, 0.6);
        }

        /* Enhanced dropdown styling */
        select:focus {
          background: rgba(255, 255, 255, 0.15);
          border-color: #6366F1;
          color: white;
          box-shadow: 0 0 0 0.2rem rgba(99, 102, 241, 0.25);
          outline: none;
        }

        /* Custom scrollbar for dropdown */
        select::-webkit-scrollbar {
          width: 8px;
        }

        select::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }

        select::-webkit-scrollbar-thumb {
          background: #6366F1;
          border-radius: 4px;
        }

        select::-webkit-scrollbar-thumb:hover {
          background: #8B5CF6;
        }

        /* Dark theme for dropdown options */
        select option {
          background: #302b63;
          color: white;
          padding: 10px;
          border: none;
        }

        select option:hover {
          background: #6366F1;
        }

        /* Loading spinner animation */
        .loading-spinner {
          animation: spin 1s linear infinite;
        }

        /* Floating orbs animation */
        .floating-orb {
          animation: float 8s ease-in-out infinite;
        }

        /* Alert close button hover */
        .alert-close:hover {
          background: rgba(239, 68, 68, 0.2);
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr !important;
          }
          
          .page-title {
            font-size: 2rem !important;
          }
          
          .header, .content {
            padding: 20px !important;
          }
          
          .card-body {
            padding: 20px !important;
          }
        }

        @media (max-width: 480px) {
          .greeting {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px !important;
          }
          
          .welcome-badge {
            align-self: flex-start;
          }
        }

        /* Smooth transitions for all interactive elements */
        .form-input, .submit-btn, .back-btn, select {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Focus visible for accessibility */
        .form-input:focus-visible,
        select:focus-visible,
        .submit-btn:focus-visible,
        .back-btn:focus-visible {
          outline: 2px solid #6366F1;
          outline-offset: 2px;
        }

        /* Disabled state styling */
        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
        }

        /* Selection styling */
        ::selection {
          background: rgba(99, 102, 241, 0.3);
          color: white;
        }

        /* Scrollbar styling for the entire page */
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
        }

        ::-webkit-scrollbar-thumb {
          background: #6366F1;
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #8B5CF6;
        }
      `}</style>
    </Layout>
  );   
}

export default UpdateTask;